import { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { supabase } from '@/lib/supabase';
import ResumoTab from '@/pages/condominios/detail/components/ResumoTab';
import FormularioDinamico from '@/components/feature/FormularioDinamico';
import TorresTab from '@/pages/condominios/detail/components/TorresTab';
import ImoveisTab from '@/pages/condominios/detail/components/ImoveisTab';
import FuncionariosTab from '@/pages/condominios/detail/components/FuncionariosTab';
import AnexosTab from '@/pages/condominios/detail/components/AnexosTab';
import InfraestruturaTab from '@/pages/condominios/detail/components/InfraestruturaTab';
import SegurancaTab from '@/pages/condominios/detail/components/SegurancaTab';
import LicencasTab from '@/pages/condominios/detail/components/LicencasTab';
import ManutencoesTab from '@/pages/condominios/detail/components/ManutencoesTab';
import AlvarasTab from '@/pages/condominios/detail/components/AlvarasTab';
import ConselheirosTab from '@/pages/condominios/detail/components/ConselheirosTab';
import ReunioesTab from '@/pages/condominios/detail/components/ReunioesTab';

interface CondoData {
  id: string;
  nome: string;
  razao_social?: string;
  comentarios?: string;
  endereco?: {
    cep: string;
    logradouro: string;
    numero: string;
    complemento?: string;
    bairro: string;
    cidade: string;
    estado: string;
  };
  administradora?: any;
  sindico?: any;
  localizacao?: string;
}

const TABS = [
  { key: 'resumo', label: 'Resumo', icon: 'ri-home-4-line' },
  { key: 'torres', label: 'Torres', icon: 'ri-building-4-line' },
  { key: 'imoveis', label: 'Imóveis', icon: 'ri-building-line' },
  { key: 'funcionarios', label: 'Funcionários', icon: 'ri-team-line' },
  { key: 'conselheiros', label: 'Conselheiros', icon: 'ri-group-line' },
  { key: 'reunioes', label: 'Reuniões', icon: 'ri-calendar-event-line' },
  { key: 'infraestrutura', label: 'Infraestrutura', icon: 'ri-tools-line' },
  { key: 'manutencoes', label: 'Manutenções', icon: 'ri-settings-line' },
  { key: 'anexos', label: 'Anexos', icon: 'ri-attachment-2' },
  { key: 'seguranca', label: 'Segurança', icon: 'ri-shield-check-line' },
  { key: 'licencas', label: 'Licenças', icon: 'ri-file-list-3-line' },
  { key: 'alvaras', label: 'Alvarás', icon: 'ri-file-shield-2-line' },
  { key: 'questionario', label: 'Questionário', icon: 'ri-survey-line' },
];

export default function CondominioDetailPage() {
  const { id, tab } = useParams<{ id: string; tab?: string }>();
  const activeTab = tab || 'resumo';
  const [condo, setCondo] = useState<CondoData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCondo = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: fErr } = await supabase
        .from('condominios')
        .select('*')
        .eq('id', id)
        .single();

      if (fErr) {
        setError(fErr.message);
      } else if (data) {
        setCondo(data as CondoData);
      } else {
        setError('Condomínio não encontrado.');
      }
    } catch (e: any) {
      setError(e.message || 'Erro ao carregar condomínio');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) void fetchCondo();
  }, [id, fetchCondo]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="max-w-6xl flex items-center justify-center py-20">
          <div className="flex flex-col items-center gap-4">
            <div className="w-10 h-10 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-sm text-foreground-500">Carregando condomínio...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !condo) {
    return (
      <DashboardLayout>
        <div className="max-w-6xl">
          <div className="mb-6">
            <Link
              to="/condominios"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-background-300/60 text-sm text-foreground-700 hover:bg-background-100 transition-colors"
            >
              <i className="ri-arrow-left-s-line"></i>Condomínios
            </Link>
          </div>
          <div className="flex flex-col items-center gap-4 py-20 text-center">
            <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center">
              <i className="ri-error-warning-line text-red-500 text-2xl"></i>
            </div>
            <h2 className="text-lg font-semibold text-foreground-900">Condomínio não encontrado</h2>
            <p className="text-sm text-foreground-600 max-w-md">
              {error || 'O condomínio que você procura não existe ou foi removido.'}
            </p>
            <button
              onClick={fetchCondo}
              className="px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              Tentar novamente
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const estado = condo.endereco?.estado || '';
  const cidade = condo.endereco?.cidade || '';

  return (
    <DashboardLayout>
      <div className="max-w-6xl">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Link
              to="/condominios"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-background-300/60 text-sm text-foreground-700 hover:bg-background-100 transition-colors"
            >
              <i className="ri-arrow-left-s-line"></i>Condomínios
            </Link>
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded bg-primary-50 flex items-center justify-center">
                <i className="ri-building-line text-primary-600 text-xs"></i>
              </div>
              <h1 className="text-xl md:text-2xl font-bold text-foreground-900">{condo.nome}</h1>
              <span className="text-sm text-foreground-500">
                — {cidade}, {estado}
              </span>
            </div>
          </div>

          {/* Tab navigation removed — now only via sidebar */}
        </div>

        <div className="flex-1 min-w-0">
          {activeTab === 'questionario' && id && (
            <FormularioDinamico chave="questionario-bombeiros" condominioId={id} />
          )}
          {activeTab === 'resumo' && <ResumoTab condo={condo} onSalvo={fetchCondo} />}
          {activeTab === 'torres' && <TorresTab />}
          {activeTab === 'imoveis' && <ImoveisTab condoId={id || ''} />}
          {activeTab === 'funcionarios' && <FuncionariosTab />}
          {activeTab === 'conselheiros' && <ConselheirosTab />}
          {activeTab === 'reunioes' && <ReunioesTab />}
          {activeTab === 'infraestrutura' && <InfraestruturaTab />}
          {activeTab === 'manutencoes' && <ManutencoesTab condoId={id || ''} />}
          {activeTab === 'anexos' && <AnexosTab />}
          {activeTab === 'seguranca' && <SegurancaTab condoId={id || ''} />}
          {activeTab === 'licencas' && <LicencasTab condoId={id || ''} />}
          {activeTab === 'alvaras' && (
            <AlvarasTab estado={estado} cidade={cidade} condoId={id || ''} />
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
